// Light markup for glossary definitions and notice sections (mockup ui.md / ui.mdInline / ui.mdBlock):
// paragraphs · '- ' lists · '1. ' lists · '# ' sub-headings (drawn as bold paragraphs, not headings) · **bold** · `code`.
// Text only — never HTML.
import type { ReactNode } from 'react';
import { mdBlocks, type MdBlock } from '../lib/util';

const INLINE = /\*\*(?=\S)([\s\S]*?\S)\*\*|`([^`\n]+)`/g;

export function mdInline(s: string, prefix = 'md'): ReactNode[] {
  const out: ReactNode[] = [];
  const str = String(s ?? '');
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  let k = 0;
  while ((m = INLINE.exec(str))) {
    if (m.index > last) out.push(str.slice(last, m.index));
    out.push(m[1] != null ? <strong key={k++}>{m[1]}</strong> : <code key={k++} className={`${prefix}__code`}>{m[2]}</code>);
    last = INLINE.lastIndex;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

export function MdBlockView({ block, prefix = 'md' }: { block: MdBlock; prefix?: string }) {
  if ('items' in block) {
    const Tag = block.type;
    return (
      <Tag className={`${prefix}__list`}>
        {block.items.map((x, i) => (
          <li key={i}>{mdInline(x, prefix)}</li>
        ))}
      </Tag>
    );
  }
  return <p className={block.type === 'h' ? `${prefix}__h` : undefined}>{mdInline(block.text, prefix)}</p>;
}

/** One paragraph → just the inline text in `tag`; several blocks → a .md wrapper (div, because lists cannot sit in <p>). */
export function Md({ src, prefix = 'md', tag = 'div', wrap, className }: { src: string | null | undefined; prefix?: string; tag?: 'div' | 'p' | 'span'; wrap?: boolean; className?: string }) {
  const blocks = mdBlocks(src);
  const first = blocks[0];
  if (!wrap && blocks.length <= 1 && (!first || first.type === 'p')) {
    const Tag = tag;
    return <Tag className={className}>{mdInline(first && 'text' in first ? first.text : '', prefix)}</Tag>;
  }
  const Tag = tag === 'p' ? 'div' : tag;
  return (
    <Tag className={className ? `${prefix} ${className}` : prefix}>
      {blocks.map((b, i) => (
        <MdBlockView key={i} block={b} prefix={prefix} />
      ))}
    </Tag>
  );
}
